import { useFormikContext } from "formik";

import type { MasteryPromptLevel, PromptHierarchy, TargetFormValues, TeachingProcedure } from "../TargetBuilderPanel.types";
import { PROMPT_PRESETS } from "../TargetBuilderPanel.types";

/**
 * Helpers to read/write the prompt hierarchy inside the current teaching
 * procedure. Returns no-ops if the procedure type doesn't support a hierarchy
 * (e.g. free-operant, structured-arrangement).
 */
export const usePromptHierarchy = () => {
  const { values, setFieldValue } = useFormikContext<TargetFormValues>();
  const procedure = values.teaching_procedure;

  const hasHierarchy =
    procedure?.type === "discrete-trial" ||
    procedure?.type === "net" ||
    procedure?.type === "task-analysis";

  const hierarchy: PromptHierarchy | null = hasHierarchy
    ? (procedure as Extract<TeachingProcedure, { prompt_hierarchy: PromptHierarchy }>).prompt_hierarchy
    : null;

  const update = (next: PromptHierarchy) => {
    if (!hasHierarchy) return;
    setFieldValue("teaching_procedure", { ...procedure, prompt_hierarchy: next });
  };

  const toggleLevel = (code: string, checked: boolean) => {
    if (!hierarchy) return;
    update({
      ...hierarchy,
      levels: checked
        ? Array.from(new Set([...hierarchy.levels, code]))
        : hierarchy.levels.filter((c) => c !== code),
      preset: undefined,
    });
  };

  const applyPreset = (presetKey: string) => {
    const preset = PROMPT_PRESETS.find((p) => p.key === presetKey);
    if (!preset || !hierarchy) return;
    update({ ...hierarchy, levels: preset.levels, preset: preset.key });
  };

  const setCountPromptedAsCorrect = (checked: boolean) => {
    if (!hierarchy) return;
    update({ ...hierarchy, count_prompted_as_correct: checked });
  };

  const setMasteryPromptLevel = (level: MasteryPromptLevel | "") => {
    if (!hierarchy) return;
    update({ ...hierarchy, mastery_prompt_level: level });
  };

  return {
    hierarchy,
    hasHierarchy,
    toggleLevel,
    applyPreset,
    setCountPromptedAsCorrect,
    setMasteryPromptLevel,
  };
};
